import {
  Box,
  Typography,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { Cancel, FilterList, ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  PersonSortKey,
  SortOrder,
  SubmissionStatus,
  SubmissionType,
  Tenant,
  UserGroup,
  useDebounce,
  useGetBrokerOptions,
  useGetEntities,
  useGetEntitiesBrokeragesOptions,
  useGetProgramsQuery,
  useGetUsers,
  useSubmissionRenewalType,
} from '@converge/graphql';
import {
  CheckedState,
  FilterState,
  Option,
} from '../../presentation/components/table-filter';
import { useInfiniteScrollFilter } from '../../hooks';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { NewConvergeButton } from '../../presentation';
import { capitalizeLetterRemoveUnderLine } from '@converge/utils';

export interface SubmissionFilterOptions {
  broker: Option[];
  brokerage: Option[];
  tenant: Option[];
  type: Option[];
  program: Option[];
  partner: Option[];
  receivedAt: Option[];
  renewalType: Option[];
  status: Option[];
  submissionType: Option[];
  underwriter: Option[];
  underwriterAssistant: Option[];
  dueDate: Option[];
  policyStartDate: Option[];
}

const defaultFilters: CheckedState = {
  broker: [],
  brokerage: [],
  tenant: [],
  dueDate: [],
  policyStartDate: [],
  partner: [],
  type: [],
  program: [],
  receivedAt: [],
  renewalType: [],
  status: [],
  submissionType: [],
  underwriter: [],
  underwriterAssistant: [],
};

interface SubmissionFilterProps {
  currentFilters: CheckedState;
  setCurrentFilters: (filters: CheckedState) => void;
  currentFilterLabels: CheckedState;
  setCurrentFilterLabels: (filters: CheckedState) => void;
  filterLabels: CheckedState;
  setFilterLabels: (filters: CheckedState) => void;
  setSubmissionFilters?: (filters: CheckedState) => void;
  resetPageNumber: () => void;
}

const DATE_FIELDS = ['receivedAt', 'dueDate', 'policyStartDate'] as const;
type DateField = (typeof DATE_FIELDS)[number];

type DateRange = [Dayjs | null, Dayjs | null];

const SubmissionFilter = ({
  currentFilters,
  setCurrentFilters,
  currentFilterLabels,
  setCurrentFilterLabels,
  filterLabels,
  setFilterLabels,
  setSubmissionFilters,
  resetPageNumber,
}: SubmissionFilterProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [pendingFilters, setPendingFilters] =
    useState<CheckedState>(currentFilters);
  const [pendingFilterLabels, setPendingFilterLabels] =
    useState<CheckedState>(currentFilterLabels);

  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>(
    {
      broker: false,
      brokerage: false,
      tenant: false,
      dueDate: false,
      policyStartDate: false,
      partner: false,
      type: false,
      program: false,
      receivedAt: false,
      renewalType: false,
      status: false,
      submissionType: false,
      underwriter: false,
      underwriterAssistant: false,
    }
  );

  const [dateRanges, setDateRanges] = useState<Record<DateField, DateRange>>({
    receivedAt: [null, null],
    dueDate: [null, null],
    policyStartDate: [null, null],
  });

  const [dateErrors, setDateErrors] = useState<Record<DateField, string>>({
    receivedAt: '',
    dueDate: '',
    policyStartDate: '',
  });

  const emptyOptions: SubmissionFilterOptions = {
    broker: [],
    brokerage: [],
    tenant: [],
    dueDate: [],
    policyStartDate: [],
    partner: [],
    type: [],
    program: [],
    receivedAt: [],
    renewalType: [],
    status: [],
    submissionType: [],
    underwriter: [],
    underwriterAssistant: [],
  };

  const [allOptions, setAllOptions] =
    useState<SubmissionFilterOptions>(emptyOptions);
  const [filterOptions, setFilterOptions] =
    useState<SubmissionFilterOptions>(emptyOptions);

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    broker: '',
    brokerage: '',
    tenant: '',
    partner: '',
    type: '',
    program: '',
    receivedAt: '',
    renewalType: '',
    status: '',
    submissionType: '',
    underwriter: '',
    underwriterAssistant: '',
  });

  const debouncedBrokerageName = useDebounce<string>(
    searchFilters['brokerage'],
    300
  );
  const debouncedBrokerName = useDebounce<string>(searchFilters['broker'], 300);
  const debouncedUwName = useDebounce<string>(
    searchFilters['underwriter'],
    300
  );
  const debouncedUaName = useDebounce<string>(
    searchFilters['underwriterAssistant'],
    300
  );

  // Brokerage — infinite scroll
  const {
    loading: brokerageLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    data: brokerageData,
  } = useGetEntitiesBrokeragesOptions({
    pageNumber: 0,
    pageSize: 50,
    enabled: modalOpen,
    brokerageName: debouncedBrokerageName,
  });

  // Broker — infinite scroll
  const {
    loading: brokerLoading,
    hasNextPage: hasMoreBrokers,
    fetchMore: fetchMoreBrokers,
    data: brokerData,
  } = useGetBrokerOptions({
    pageNumber: 0,
    pageSize: 50,
    firstName: debouncedBrokerName,
    enabled: modalOpen,
    sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
  });

  // Underwriter users — infinite scroll
  const {
    loading: uwLoading,
    hasNextPage: hasMoreUw,
    fetchMore: fetchMoreUw,
    data: uwData,
  } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
    fullName: debouncedUwName,
    pageSize: 50,
    enabled: modalOpen,
    sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
  });

  // Underwriter assistant users — infinite scroll
  const {
    loading: uaLoading,
    hasNextPage: hasMoreUa,
    fetchMore: fetchMoreUa,
    data: uaData,
  } = useGetUsers({
    userGroups: [UserGroup.UnderwriterAssistant],
    fullName: debouncedUaName,
    pageSize: 50,
    enabled: modalOpen,
    sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
  });

  // Static options
  const { data: renewalTypeData } = useSubmissionRenewalType({
    enabled: modalOpen,
  });
  const { data: programsData } = useGetProgramsQuery({ enabled: modalOpen });
  const { entities: partners = [] } = useGetEntities({
    isProgramPartner: true,
    enabled: modalOpen,
  });

  // --- Infinite scroll hooks ---

  const {
    options: allBrokerages,
    loadingMore: loadingMoreBrokerages,
    handleScroll: handleBrokerageScroll,
  } = useInfiniteScrollFilter({
    data: brokerageData,
    loading: brokerageLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    transformData: (data) => {
      if (!data?.entities?.edges) return [];
      return data.entities.edges.map((edge) => {
        const node = edge.node;
        const aliases = node.aliases?.edges || [];
        const legalNameAlias = aliases.find(
          (a) => a.node.primary && a.node.type === 'LEGAL_NAME'
        );
        const tradingNameAlias = aliases.find(
          (a) => a.node.primary && a.node.type === 'TRADING_NAME'
        );
        let label = node.referenceId || 'N/A';
        if (legalNameAlias?.node.name?.trim()) {
          label = legalNameAlias.node.name;
        } else if (tradingNameAlias?.node.name) {
          label = tradingNameAlias.node.name;
        }
        return { value: node.uuid || '-', label };
      });
    },
    searchTerm: debouncedBrokerageName,
    selectedValues: pendingFilters['brokerage'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: pageSize * nextPage, first: pageSize },
      filters: {
        agency: true,
        ...(searchTerm && { brokerageName: searchTerm }),
      },
      aliasFilters: {
        source: 'PRIMARY',
        ...(searchTerm && { name: searchTerm }),
      },
    }),
  });

  const {
    options: allBrokers,
    loadingMore: loadingMoreBrokers,
    handleScroll: handleBrokerScroll,
  } = useInfiniteScrollFilter({
    data: brokerData,
    loading: brokerLoading,
    hasNextPage: hasMoreBrokers,
    fetchMore: fetchMoreBrokers,
    transformData: (data) => {
      if (!data?.persons?.edges) return [];
      return data.persons.edges.map((edge) => {
        const node = edge.node;
        const isInactive = node.status === 'INACTIVE';
        const baseName = `${node.firstName || ''} ${
          node.lastName || ''
        }`.trim();
        const label = isInactive ? `${baseName} (Inactive)` : baseName;
        return { value: node.uuid || '--', label };
      });
    },
    searchTerm: debouncedBrokerName,
    selectedValues: pendingFilters['broker'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: pageSize * nextPage, first: pageSize },
      filters: {
        agent: true,
        ...(searchTerm && { fullName: searchTerm }),
      },
      sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
    }),
  });

  const transformUsers = (data: typeof uwData) => {
    if (!data?.users?.edges) return [];
    return data.users.edges.map((edge) => ({
      value: edge.node.uuid || '',
      label: `${edge.node.firstName || ''} ${edge.node.lastName || ''}`.trim(),
    }));
  };

  const {
    options: allUws,
    loadingMore: loadingMoreUw,
    handleScroll: handleUwScroll,
  } = useInfiniteScrollFilter({
    data: uwData,
    loading: uwLoading,
    hasNextPage: hasMoreUw,
    fetchMore: fetchMoreUw,
    transformData: transformUsers,
    searchTerm: debouncedUwName,
    selectedValues: pendingFilters['underwriter'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: pageSize * nextPage, first: pageSize },
      filters: {
        groups: [UserGroup.Underwriter],
        ...(searchTerm && { fullName: searchTerm }),
      },
      sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
    }),
  });

  const {
    options: allUas,
    loadingMore: loadingMoreUa,
    handleScroll: handleUaScroll,
  } = useInfiniteScrollFilter({
    data: uaData,
    loading: uaLoading,
    hasNextPage: hasMoreUa,
    fetchMore: fetchMoreUa,
    transformData: transformUsers,
    searchTerm: debouncedUaName,
    selectedValues: pendingFilters['underwriterAssistant'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: pageSize * nextPage, first: pageSize },
      filters: {
        groups: [UserGroup.UnderwriterAssistant],
        ...(searchTerm && { fullName: searchTerm }),
      },
      sort: { key: PersonSortKey.FirstName, order: SortOrder.Asc },
    }),
  });

  // Sync infinite scroll results into filter options
  useEffect(() => {
    setFilterOptions((prev) => ({ ...prev, brokerage: allBrokerages }));
    setAllOptions((prev) => ({ ...prev, brokerage: allBrokerages }));
  }, [allBrokerages]);

  useEffect(() => {
    setFilterOptions((prev) => ({ ...prev, broker: allBrokers }));
    setAllOptions((prev) => ({ ...prev, broker: allBrokers }));
  }, [allBrokers]);

  const UNASSIGNED_OPTION: Option = {
    value: 'unassigned',
    label: 'Unassigned',
  };

  useEffect(() => {
    const underwriterOptionsWithUnassigned = [UNASSIGNED_OPTION, ...allUws];
    setFilterOptions((prev) => ({
      ...prev,
      underwriter: underwriterOptionsWithUnassigned,
    }));
    setAllOptions((prev) => ({
      ...prev,
      underwriter: underwriterOptionsWithUnassigned,
    }));
  }, [allUws]);

  useEffect(() => {
    const underwriterAssistantOptionsWithUnassigned = [
      UNASSIGNED_OPTION,
      ...allUas,
    ];
    setFilterOptions((prev) => ({
      ...prev,
      underwriterAssistant: underwriterAssistantOptionsWithUnassigned,
    }));
    setAllOptions((prev) => ({
      ...prev,
      underwriterAssistant: underwriterAssistantOptionsWithUnassigned,
    }));
  }, [allUas]);

  // Build static option lists
  const statusOptions = useMemo<Option[]>(
    () =>
      Object.entries(SubmissionStatus)
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([, value]) => ({
          value,
          label: capitalizeLetterRemoveUnderLine(value),
        })),
    []
  );

  const renewalTypeOptions = useMemo<Option[]>(() => {
    return (
      renewalTypeData?.submissionRenewalTypes?.edges
        .map((e: { node: { name: string } }) => ({
          value: e.node.name,
          label: capitalizeLetterRemoveUnderLine(e.node.name),
        }))
        .sort((a: Option, b: Option) => a.label.localeCompare(b.label)) || []
    );
  }, [renewalTypeData]);

  const submissionTypeOptions = useMemo<Option[]>(
    () => [
      { value: 'newBusiness', label: 'New Business' },
      { value: 'renewal', label: 'Renewal' },
    ],
    []
  );

  const typeOptions = useMemo<Option[]>(
    () =>
      Object.entries(SubmissionType).map(([, value]) => ({
        value,
        label: capitalizeLetterRemoveUnderLine(value),
      })),
    []
  );

  const tenantOptions = useMemo<Option[]>(
    () =>
      Object.entries(Tenant)
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([key, value]) => ({ value, label: key })),
    []
  );

  const programOptions = useMemo<Option[]>(
    () =>
      programsData?.programs.edges.map((e) => ({
        value: e.node.uuid ?? '',
        label: e.node.name ?? '',
      })) || [],
    [programsData]
  );

  const partnerOptions = useMemo<Option[]>(
    () =>
      partners
        .map((p) => ({ value: p?.uuid ?? '', label: p?.name ?? 'N/A' }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [partners]
  );

  // Sync static options when they load
  useEffect(() => {
    if (!statusOptions.length) return;
    setAllOptions((prev) => ({ ...prev, status: statusOptions }));
    setFilterOptions((prev) => ({ ...prev, status: statusOptions }));
  }, [statusOptions]);

  useEffect(() => {
    if (!renewalTypeOptions.length) return;
    setAllOptions((prev) => ({ ...prev, renewalType: renewalTypeOptions }));
    setFilterOptions((prev) => ({ ...prev, renewalType: renewalTypeOptions }));
  }, [renewalTypeOptions]);

  useEffect(() => {
    setAllOptions((prev) => ({
      ...prev,
      submissionType: submissionTypeOptions,
    }));
    setFilterOptions((prev) => ({
      ...prev,
      submissionType: submissionTypeOptions,
    }));
  }, []);

  useEffect(() => {
    setAllOptions((prev) => ({ ...prev, type: typeOptions }));
    setFilterOptions((prev) => ({ ...prev, type: typeOptions }));
  }, []);

  useEffect(() => {
    setAllOptions((prev) => ({ ...prev, tenant: tenantOptions }));
    setFilterOptions((prev) => ({ ...prev, tenant: tenantOptions }));
  }, []);

  useEffect(() => {
    if (!programOptions.length) return;
    setAllOptions((prev) => ({ ...prev, program: programOptions }));
    setFilterOptions((prev) => ({ ...prev, program: programOptions }));
  }, [programOptions]);

  useEffect(() => {
    if (!partnerOptions.length) return;
    setAllOptions((prev) => ({ ...prev, partner: partnerOptions }));
    setFilterOptions((prev) => ({ ...prev, partner: partnerOptions }));
  }, [partnerOptions]);

  // --- Modal open / close ---

  const handleModalOpen = () => {
    setModalOpen(true);
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
    setSearchFilters({
      broker: '',
      brokerage: '',
      tenant: '',
      partner: '',
      type: '',
      program: '',
      receivedAt: '',
      renewalType: '',
      status: '',
      submissionType: '',
      underwriter: '',
      underwriterAssistant: '',
    });

    // Restore date range state from saved filters
    const newDateRanges: Record<DateField, DateRange> = {
      receivedAt: [null, null],
      dueDate: [null, null],
      policyStartDate: [null, null],
    };
    DATE_FIELDS.forEach((field) => {
      try {
        const saved = currentFilters[field];
        if (saved?.length > 0) {
          const parsed = JSON.parse(saved[0]);
          newDateRanges[field] = [
            parsed.start ? dayjs(parsed.start) : null,
            parsed.end ? dayjs(parsed.end) : null,
          ];
        }
      } catch {
        /* ignore */
      }
    });
    setDateRanges(newDateRanges);
    setDateErrors({ receivedAt: '', dueDate: '', policyStartDate: '' });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchFilters({
      broker: '',
      brokerage: '',
      tenant: '',
      partner: '',
      type: '',
      program: '',
      receivedAt: '',
      renewalType: '',
      status: '',
      submissionType: '',
      underwriter: '',
      underwriterAssistant: '',
    });
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
    setDateRanges({
      receivedAt: [null, null],
      dueDate: [null, null],
      policyStartDate: [null, null],
    });
    setDateErrors({ receivedAt: '', dueDate: '', policyStartDate: '' });
  };

  const handleSaveFilter = () => {
    setCurrentFilters(pendingFilters);
    setCurrentFilterLabels(pendingFilterLabels);
    setFilterLabels(pendingFilterLabels);
    setSubmissionFilters && setSubmissionFilters(pendingFilters);
    localStorage.setItem('submissionFilters', JSON.stringify(pendingFilters));
    localStorage.setItem(
      'submissionFilterLabels',
      JSON.stringify(pendingFilterLabels)
    );
    resetPageNumber();
    handleModalClose();
  };

  const handleClearAllFilters = () => {
    setCurrentFilters(defaultFilters);
    setCurrentFilterLabels(defaultFilters);
    setFilterLabels(defaultFilters);
    setPendingFilters(defaultFilters);
    setPendingFilterLabels(defaultFilters);
    setSubmissionFilters && setSubmissionFilters(defaultFilters);
    setDateRanges({
      receivedAt: [null, null],
      dueDate: [null, null],
      policyStartDate: [null, null],
    });
    setDateErrors({ receivedAt: '', dueDate: '', policyStartDate: '' });
    localStorage.removeItem('submissionFilters');
    localStorage.removeItem('submissionFilterLabels');
    resetPageNumber();
  };

  const handleCheckboxChange = (
    column: keyof SubmissionFilterOptions,
    option: Option
  ) => {
    const currentChecked = Array.isArray(pendingFilters[column])
      ? pendingFilters[column]
      : [];
    const currentLabels = Array.isArray(pendingFilterLabels[column])
      ? pendingFilterLabels[column]
      : [];

    let updatedFilter: string[];
    let updatedFilterLabel: string[];

    if (currentChecked.includes(option.value)) {
      updatedFilter = currentChecked.filter((item) => item !== option.value);
      updatedFilterLabel = currentLabels.filter(
        (label) => label !== option.label
      );
    } else {
      updatedFilter = [...currentChecked, option.value];
      updatedFilterLabel = [...currentLabels, option.label];
    }

    setPendingFilters({ ...pendingFilters, [column]: updatedFilter });
    setPendingFilterLabels({
      ...pendingFilterLabels,
      [column]: updatedFilterLabel,
    });
  };

  const handleAccordionChange = (panel: string) => {
    setExpandedPanels({ ...expandedPanels, [panel]: !expandedPanels[panel] });
  };

  // --- Date range helpers ---

  const updateDateFilter = (
    field: DateField,
    from: Dayjs | null,
    to: Dayjs | null
  ) => {
    if ((from && !to) || (!from && to)) {
      setDateErrors((prev) => ({
        ...prev,
        [field]: 'Both date fields are required',
      }));
      setPendingFilters((prev) => ({ ...prev, [field]: [] }));
      setPendingFilterLabels((prev) => ({ ...prev, [field]: [] }));
    } else if (from && to) {
      if (to.isBefore(from, 'day')) {
        setDateErrors((prev) => ({
          ...prev,
          [field]: 'End date must be greater than or equal to start date',
        }));
        setPendingFilters((prev) => ({ ...prev, [field]: [] }));
        setPendingFilterLabels((prev) => ({ ...prev, [field]: [] }));
      } else {
        setDateErrors((prev) => ({ ...prev, [field]: '' }));
        const rangeLabel = `${from.format('MM/DD/YYYY')} - ${to.format(
          'MM/DD/YYYY'
        )}`;
        const rangeValue = JSON.stringify({
          start: from.format('YYYY-MM-DD'),
          end: to.format('YYYY-MM-DD'),
        });
        setPendingFilters((prev) => ({ ...prev, [field]: [rangeValue] }));
        setPendingFilterLabels((prev) => ({ ...prev, [field]: [rangeLabel] }));
      }
    } else {
      setDateErrors((prev) => ({ ...prev, [field]: '' }));
      setPendingFilters((prev) => ({ ...prev, [field]: [] }));
      setPendingFilterLabels((prev) => ({ ...prev, [field]: [] }));
    }
  };

  // --- Active filter chips ---

  const filterKeyLabels: Record<string, string> = {
    broker: 'Broker',
    brokerage: 'Brokerage',
    tenant: 'Digital Source',
    dueDate: 'Due Date',
    policyStartDate: 'Effective Date',
    partner: 'Partner',
    type: 'Policy Type',
    program: 'Program',
    receivedAt: 'Received At',
    renewalType: 'Renewal Type',
    status: 'Status',
    submissionType: 'Submission Type',
    underwriter: 'Underwriter',
    underwriterAssistant: 'Underwriting Assistant',
  };

  const renderActiveFilters = () => {
    const activeFilters = Object.keys(currentFilterLabels).filter(
      (key) => currentFilterLabels[key]?.length > 0
    );
    if (activeFilters.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
        {activeFilters.map((filterKey) => {
          const labels = currentFilterLabels[filterKey] || [];
          const isDateField = DATE_FIELDS.includes(filterKey as DateField);
          const filterLabel = filterKeyLabels[filterKey] || filterKey;

          return labels.map((label: string, index: number) => {
            let displayLabel = isDateField ? `${filterLabel}: ${label}` : label;
            if (label === 'Unassigned') {
              if (filterKey === 'underwriter') displayLabel = 'UW: Unassigned';
              else if (filterKey === 'underwriterAssistant')
                displayLabel = 'UA: Unassigned';
            }
            return (
              <Box
                key={`${filterKey}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', mr: '22px' }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '14px' }}
                >
                  {displayLabel}
                </Typography>
                <Cancel
                  sx={{ ml: 1, fontSize: '16px', cursor: 'pointer' }}
                  data-testid={`remove-${filterKey}-${index}-filter-button`}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (isDateField) {
                      const updatedFilters = {
                        ...currentFilters,
                        [filterKey]: [],
                      };
                      const updatedLabels = {
                        ...currentFilterLabels,
                        [filterKey]: [],
                      };
                      setCurrentFilters(updatedFilters);
                      setCurrentFilterLabels(updatedLabels);
                      setFilterLabels(updatedLabels);
                      setPendingFilters(updatedFilters);
                      setPendingFilterLabels(updatedLabels);
                      setDateRanges((prev) => ({
                        ...prev,
                        [filterKey]: [null, null],
                      }));
                      setSubmissionFilters &&
                        setSubmissionFilters(updatedFilters);
                      localStorage.setItem(
                        'submissionFilters',
                        JSON.stringify(updatedFilters)
                      );
                      localStorage.setItem(
                        'submissionFilterLabels',
                        JSON.stringify(updatedLabels)
                      );
                      resetPageNumber();
                      return;
                    }

                    const optionToRemove = allOptions[
                      filterKey as keyof SubmissionFilterOptions
                    ]?.find((opt: Option) => opt.label === label);
                    let valueToRemove: string | undefined;
                    if (optionToRemove) {
                      valueToRemove = optionToRemove.value;
                    } else {
                      const labelIndex = (
                        currentFilterLabels[filterKey] || []
                      ).indexOf(label);
                      if (labelIndex !== -1) {
                        valueToRemove = currentFilters[filterKey]?.[labelIndex];
                      }
                    }

                    if (valueToRemove) {
                      const updatedFilters = {
                        ...currentFilters,
                        [filterKey]: (currentFilters[filterKey] || []).filter(
                          (v) => v !== valueToRemove
                        ),
                      };
                      const updatedLabels = {
                        ...currentFilterLabels,
                        [filterKey]: (
                          currentFilterLabels[filterKey] || []
                        ).filter((l) => l !== label),
                      };
                      setCurrentFilters(updatedFilters);
                      setCurrentFilterLabels(updatedLabels);
                      setFilterLabels(updatedLabels);
                      setPendingFilters(updatedFilters);
                      setPendingFilterLabels(updatedLabels);
                      setSubmissionFilters &&
                        setSubmissionFilters(updatedFilters);
                      localStorage.setItem(
                        'submissionFilters',
                        JSON.stringify(updatedFilters)
                      );
                      localStorage.setItem(
                        'submissionFilterLabels',
                        JSON.stringify(updatedLabels)
                      );
                      resetPageNumber();
                    }
                  }}
                />
              </Box>
            );
          });
        })}
      </Box>
    );
  };

  // --- Accordion rendering ---

  const INFINITE_SCROLL_COLUMNS = [
    'brokerage',
    'broker',
    'underwriter',
    'underwriterAssistant',
  ];

  const columnScrollHandlerMap: Partial<
    Record<
      keyof SubmissionFilterOptions,
      (e: React.UIEvent<HTMLDivElement>) => void
    >
  > = {
    brokerage: handleBrokerageScroll,
    broker: handleBrokerScroll,
    underwriter: handleUwScroll,
    underwriterAssistant: handleUaScroll,
  };

  const columnLoadingMoreMap: Partial<
    Record<keyof SubmissionFilterOptions, boolean>
  > = {
    brokerage: loadingMoreBrokerages,
    broker: loadingMoreBrokers,
    underwriter: loadingMoreUw,
    underwriterAssistant: loadingMoreUa,
  };

  const columnLoadingMap: Partial<
    Record<keyof SubmissionFilterOptions, boolean>
  > = {
    brokerage: brokerageLoading,
    broker: brokerLoading,
    underwriter: uwLoading,
    underwriterAssistant: uaLoading,
  };

  const renderFilterAccordion = (
    column: keyof SubmissionFilterOptions,
    label: string,
    disabled?: boolean
  ) => {
    const isInfiniteScroll = INFINITE_SCROLL_COLUMNS.includes(column);
    const isColumnLoading = columnLoadingMap[column] ?? false;
    const selectedCount = pendingFilters[column]?.length || 0;
    const displayLabel =
      selectedCount > 0 ? `${label} (${selectedCount})` : label;
    const currentColumnOptions = filterOptions[column] || [];

    return (
      <Accordion
        key={column}
        expanded={!disabled && expandedPanels[column]}
        onChange={() => !disabled && handleAccordionChange(column)}
        sx={{
          pointerEvents: disabled ? 'none' : 'auto',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          data-testid={`${column}-accordion-header`}
          sx={{
            opacity: disabled ? 0.4 : 1,
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': { margin: '0' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ marginLeft: '38px', mb: 2, width: '85%' }}>
            <Autocomplete
              size="small"
              fullWidth
              multiple
              disableClearable={isInfiniteScroll}
              freeSolo={true}
              filterOptions={(x) => x}
              options={[]}
              open={false}
              getOptionLabel={(option): string => {
                if (typeof option === 'string') return option;
                if (option && typeof option === 'object' && 'label' in option) {
                  return String((option as Option).label || '');
                }
                return '';
              }}
              value={[] as Option[]}
              isOptionEqualToValue={(
                option: Option | string,
                value: Option | string
              ) => {
                if (!option || !value) return false;
                if (typeof option === 'string' || typeof value === 'string')
                  return false;
                return (option as Option).value === (value as Option).value;
              }}
              onChange={() => {
                /* selection handled via checkboxes */
              }}
              inputValue={searchFilters[column] || ''}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'reset') return;
                if (reason === 'clear') {
                  if (isInfiniteScroll) return;
                  setSearchFilters({ ...searchFilters, [column]: '' });
                  setFilterOptions((prev) => ({
                    ...prev,
                    [column]: allOptions[column],
                  }));
                  return;
                }
                setSearchFilters({ ...searchFilters, [column]: newInputValue });
                if (!isInfiniteScroll) {
                  if (newInputValue.trim()) {
                    const filtered = allOptions[column]?.filter((opt) =>
                      opt?.label
                        ?.toLowerCase()
                        .includes(newInputValue.toLowerCase())
                    );
                    setFilterOptions((prev) => ({
                      ...prev,
                      [column]: filtered,
                    }));
                  } else {
                    setFilterOptions((prev) => ({
                      ...prev,
                      [column]: allOptions[column],
                    }));
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search ${label}`}
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': `search-${column}-input`,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      fontWeight: '600',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'black',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                '& .MuiAutocomplete-clearIndicator': {
                  display: isInfiniteScroll ? 'none' : 'block',
                  visibility: 'visible',
                  color: 'black',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              marginLeft: '30px',
              width: '85%',
              maxHeight: '250px',
              overflow: 'auto',
            }}
            onScroll={columnScrollHandlerMap[column]}
          >
            <FormGroup>
              {isColumnLoading && currentColumnOptions.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#474343' }}>
                  Loading...
                </Typography>
              ) : currentColumnOptions.length > 0 ? (
                currentColumnOptions.map((option, index) => {
                  const isChecked = pendingFilters[column]?.includes(
                    option.value
                  );
                  return (
                    <FormControlLabel
                      key={`${option.value}-${index}`}
                      data-testid={`${column}-${option.value}-checkbox`}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(column, option)}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            paddingTop: '2px',
                            color: '#666666',
                            '&.Mui-checked': { color: '#28C0A7' },
                          }}
                        />
                      }
                      label={option.label}
                      sx={{
                        alignItems: 'flex-start',
                        margin: '2px 0',
                        '& .MuiFormControlLabel-label': {
                          fontWeight: '600',
                          fontSize: '14px',
                          paddingTop: '3px',
                        },
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: '#474343' }}>
                  No results
                </Typography>
              )}
              {columnLoadingMoreMap[column] && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderRadioAccordion = (
    column: keyof SubmissionFilterOptions,
    label: string,
    disabled?: boolean,
    onSelect?: (val: string | null) => void
  ) => {
    const selectedCount = pendingFilters[column]?.length || 0;
    const displayLabel =
      selectedCount > 0 ? `${label} (${selectedCount})` : label;
    const currentValue = pendingFilters[column]?.[0] || '';
    const currentColumnOptions = filterOptions[column] || [];

    const handleChange = (val: string) => {
      if (val === currentValue) {
        setPendingFilters((prev) => ({ ...prev, [column]: [] }));
        setPendingFilterLabels((prev) => ({ ...prev, [column]: [] }));
        onSelect?.(null);
      } else {
        const opt = currentColumnOptions.find((o) => o.value === val);
        setPendingFilters((prev) => ({ ...prev, [column]: [val] }));
        setPendingFilterLabels((prev) => ({
          ...prev,
          [column]: [opt?.label || val],
        }));
        onSelect?.(val);
      }
    };

    return (
      <Accordion
        key={column}
        expanded={!disabled && expandedPanels[column]}
        onChange={() => !disabled && handleAccordionChange(column)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
          opacity: disabled ? 0.4 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          data-testid={`${column}-accordion-header`}
          sx={{
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': { margin: '0' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ marginLeft: '30px', width: '85%' }}>
            <RadioGroup
              value={currentValue}
              onChange={(e) => handleChange(e.target.value)}
            >
              {currentColumnOptions.map((option, index) => (
                <FormControlLabel
                  key={`${option.value}-${index}`}
                  value={option.value}
                  data-testid={`${column}-${option.value}-radio`}
                  control={
                    <Radio
                      size="small"
                      onClick={() => {
                        if (option.value === currentValue)
                          handleChange(option.value);
                      }}
                      sx={{
                        color: '#666666',
                        '&.Mui-checked': { color: '#28C0A7' },
                      }}
                    />
                  }
                  label={option.label}
                  sx={{
                    alignItems: 'center',
                    margin: '2px 0',
                    '& .MuiFormControlLabel-label': {
                      fontWeight: '600',
                      fontSize: '14px',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDateAccordion = (field: DateField, label: string) => {
    const selectedCount = pendingFilters[field]?.length || 0;
    const displayLabel = selectedCount > 0 ? `${label} (1)` : label;

    return (
      <Accordion
        key={field}
        expanded={expandedPanels[field]}
        onChange={() => handleAccordionChange(field)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          data-testid={`${field}-accordion-header`}
          sx={{
            minHeight: '56px !important',
            height: '56px !important',
            padding: '0 16px !important',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': {
              margin: '0 !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: '0 !important',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              lineHeight: 1,
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                marginLeft: '38px',
                width: '87%',
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  From
                </Typography>
                <Box
                  sx={{
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': { borderColor: '#28c0a7' },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={dateRanges[field][0]}
                    maxDate={dateRanges[field][1] || undefined}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    onChange={(newValue) => {
                      const to = dateRanges[field][1];
                      setDateRanges((prev) => ({
                        ...prev,
                        [field]: [newValue, to],
                      }));
                      updateDateFilter(field, newValue, to);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: '',
                          'data-testid': `${field}-from-input`,
                        }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': { border: 'none' },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  To
                </Typography>
                <Box
                  sx={{
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': { borderColor: '#28c0a7' },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={dateRanges[field][1]}
                    minDate={dateRanges[field][0] || undefined}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    onChange={(newValue) => {
                      const from = dateRanges[field][0];
                      setDateRanges((prev) => ({
                        ...prev,
                        [field]: [from, newValue],
                      }));
                      updateDateFilter(field, from, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: '',
                          'data-testid': `${field}-to-input`,
                        }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': { border: 'none' },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
            {dateErrors[field] && (
              <Typography
                sx={{ color: '#d32f2f', fontSize: '12px', ml: '38px', mt: 1 }}
                data-testid={`${field}-error-message`}
              >
                {dateErrors[field]}
              </Typography>
            )}
          </LocalizationProvider>
        </AccordionDetails>
      </Accordion>
    );
  };

  const hasDateError = DATE_FIELDS.some((f) => dateErrors[f] !== '');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          color: 'black',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid black',
            borderRadius: '4px',
            marginRight: '10px',
          }}
          onClick={handleModalOpen}
          data-testid="open-submission-filters-button"
        >
          <FilterList sx={{ padding: '5px 5px' }} />
        </Box>

        {renderActiveFilters()}
        {Object.keys(currentFilterLabels).some(
          (key) => currentFilterLabels[key]?.length > 0
        ) && (
          <Button
            size="small"
            onClick={handleClearAllFilters}
            data-testid="clear-all-submission-filters-button"
            disableRipple
            sx={{
              ml: 0,
              textAlign: 'left',
              color: 'black',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:focus': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:active': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&.Mui-focusVisible': { backgroundColor: 'transparent' },
            }}
          >
            Clear all filters
          </Button>
        )}
      </Box>

      <Drawer
        anchor="left"
        open={modalOpen}
        onClose={handleModalClose}
        data-testid="submission-filters-drawer"
      >
        <Box
          sx={{
            height: '100vh',
            width: 400,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {renderFilterAccordion('broker', 'Broker')}
            {renderFilterAccordion('brokerage', 'Brokerage')}
            {renderFilterAccordion('tenant', 'Digital Source')}
            {renderDateAccordion('dueDate', 'Due Date')}
            {renderDateAccordion('policyStartDate', 'Effective Date')}
            {renderFilterAccordion('partner', 'Partner')}
            {renderRadioAccordion('type', 'Policy Type')}
            {renderFilterAccordion('program', 'Program')}
            {renderDateAccordion('receivedAt', 'Received At')}
            {renderFilterAccordion(
              'renewalType',
              'Renewal Type',
              pendingFilters['submissionType']?.[0] === 'newBusiness'
            )}
            {renderFilterAccordion('status', 'Status')}
            {renderRadioAccordion(
              'submissionType',
              'Submission Type',
              false,
              (val) => {
                if (val === 'newBusiness') {
                  setPendingFilters((prev) => ({ ...prev, renewalType: [] }));
                  setPendingFilterLabels((prev) => ({
                    ...prev,
                    renewalType: [],
                  }));
                }
              }
            )}
            {renderFilterAccordion('underwriter', 'Underwriter')}
            {renderFilterAccordion(
              'underwriterAssistant',
              'Underwriting Assistant'
            )}
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: 3,
              height: '39px',
            }}
          >
            <Box style={{ marginLeft: '50px' }}>
              <NewConvergeButton
                variant="outlined"
                label=" Cancel"
                onClick={handleModalClose}
                data-testid="cancel-submission-filters-button"
              />
            </Box>
            <Box sx={{ width: '137px' }}>
              <NewConvergeButton
                variant="contained"
                onClick={handleSaveFilter}
                label=" Save Filters"
                disabled={hasDateError}
                data-testid="save-submission-filters-button"
              />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default SubmissionFilter;
